import React, { useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiSwitch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Grow from '@mui/material/Grow';
import Button from '@mui/material/Button';
import { hasValue } from 'swipeclock-frontend';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';
import { useBootstrapAuth } from 'components/BootstrapAuthContext';
import {
  MOBILE_SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
} from 'components/BootstrapGate';
import ButtonPad from './ButtonPad';
import DigitalClock from './DigitalClock';
import Footer from './Footer';
import GradientContainer from './CenteredContainer';
import StyledCard from './shared/PromptContentCard';
import PoweredByFooter from './shared/PoweredByFooter';
import SignOutBar from './SignOutBar';
import OfflineStatusToolbar from './OfflineStatusToolbar';
import OfflineStatusSnackbar from './OfflineStatusSnackbar';
import OnlineStatusSnackbar from './OnlineStatusSnackbar';
import { useClockConnectData, useMissingPunches } from '../hooks/useWebclock';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { useOfflineStatusToolbar } from '../hooks/useOfflineStatusToolbar';
import { useIsMobileMode, useIsOfflineShellMode } from '../hooks/useMobileMode';
import { useOfflinePunchQueue } from '../hooks/useOfflinePunchQueue';
import { useWebClockLogout } from '../hooks/useWebClockLogout';
import { useJwtRefresh } from '../hooks/useJwtRefresh';
import { useDevOfflineOverride } from '../DevOfflineOverrideContext';

export default function ClockDashboard() {
  const theme = useTheme();
  const handleLogout = useWebClockLogout();
  const { setMissingPunches, showMissingPunchAlert } = useMissingPunches();
  const { state: whiteLabelState } = useWhiteLabelContext();
  const { backgroundColor, isEnclosedMode, labelColor } = whiteLabelState || {};
  const { data, isLoading, isError, error, isClockConnectErrorBlocking } =
    useClockConnectData();
  const { authError, sessionNotice } = useBootstrapAuth();
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const { isOffline } = useOfflinePunchQueue();
  const showOfflineWarning = isOffline && !isOfflineShellMode;

  const { canRefresh, isRefreshing, handleRefresh } = useJwtRefresh();
  const showRefreshButton = isMobileMode && canRefresh;
  const sessionNoticeText =
    isMobileMode && !canRefresh
      ? MOBILE_SESSION_EXPIRED_MESSAGE
      : SESSION_EXPIRED_MESSAGE;

  const errorMessage = error?.message || 'Error: unable to access clock data';
  const canRenderClockUi =
    !authError && !sessionNotice && !isLoading && (hasValue(data) || !isError);

  useInactivityTimeout({
    inactivity: 20,
    countdown: 0,
    onTimeout: handleLogout,
    enabled: !isEnclosedMode && !(isOfflineShellMode && isOffline),
  });

  const startColor = backgroundColor
    ? backgroundColor
    : theme.palette.custom.navyBlue.main;
  const endColor = backgroundColor
    ? backgroundColor
    : theme.palette.custom.navyBlue.light;

  const handleMissPunchClick = useCallback(() => {
    setMissingPunches(true);
  }, [setMissingPunches]);

  const handleAlertClose = useCallback(() => {
    setMissingPunches(false);
  }, [setMissingPunches]);

  const {
    visible: showOfflineStatusToolbar,
    showWifiToolbar,
    showQueueToolbar,
    isOfflineSnackbarOpen,
    isOnlineSnackbarOpen,
    pendingCount,
    hasFailedPunch,
    toggleOfflineSnackbar,
    openOfflinePunchDrawer,
    closeOnlineSnackbar,
    handleOfflineSnackbarClose,
    offlineSnackbarAutoHideDuration,
  } = useOfflineStatusToolbar();
  const {
    enabled: devOverrideEnabled,
    devIsOffline,
    setDevIsOffline,
  } = useDevOfflineOverride();

  return (
    <GradientContainer
      id="webclock-gradient-container"
      gradientStart={startColor}
      gradientEnd={endColor}
      isEnclosedMode={isEnclosedMode}
      showAlert={showMissingPunchAlert}
    >
      <StyledCard
        id="webclock-card-container"
        gap={0}
        customColor={labelColor}
        isEnclosedMode={isEnclosedMode}
      >
        {devOverrideEnabled && (
          <FormControlLabel
            control={
              <MuiSwitch
                id="sc-dev_offline_toggle"
                checked={devIsOffline}
                onChange={(event) => setDevIsOffline(event.target.checked)}
              />
            }
            label="Dev: Offline"
            sx={{ width: '100%', justifyContent: 'flex-end', pb: 1 }}
          />
        )}
        {showOfflineStatusToolbar && (
          <OfflineStatusToolbar
            pendingCount={pendingCount}
            hasFailedPunch={hasFailedPunch}
            showWifiToolbar={showWifiToolbar}
            showQueueToolbar={showQueueToolbar}
            onToggleOfflineSnackbar={toggleOfflineSnackbar}
            onOpenOfflinePunchDrawer={openOfflinePunchDrawer}
          />
        )}
        <OfflineStatusSnackbar
          autoHideDuration={offlineSnackbarAutoHideDuration}
          open={showWifiToolbar && isOfflineSnackbarOpen}
          onClose={handleOfflineSnackbarClose}
        />
        <OnlineStatusSnackbar
          open={isOnlineSnackbarOpen}
          onClose={closeOnlineSnackbar}
        />
        <DigitalClock clockAmPm />
        {!isEnclosedMode && <SignOutBar onLogout={handleLogout} />}

        <Box
          sx={{
            height: showMissingPunchAlert ? 'auto' : 0,
            overflow: 'hidden',
          }}
        >
          <Grow
            in={showMissingPunchAlert}
            style={{ transformOrigin: 'center top' }}
            {...(showMissingPunchAlert ? { timeout: 500 } : {})}
          >
            <Alert
              id="sc-missing_punch_alert"
              severity="warning"
              variant="filled"
              sx={{ width: '100%', mb: 2 }}
              onClose={handleAlertClose}
            >
              <AlertTitle>Let's get you back on track</AlertTitle>
              Please make your current punch.
            </Alert>
          </Grow>
        </Box>

        {isError && (
          <Alert id="sc-clock_connect_error_alert" severity="error">
            {errorMessage}
          </Alert>
        )}

        {showOfflineWarning && (
          <Alert id="sc-offline_warning_alert" severity="warning">
            You appear to be offline. Punches cannot be recorded until your
            connection is restored.
          </Alert>
        )}

        {authError && (
          <Alert id="sc-auth_error_alert" severity="error">
            {authError}
          </Alert>
        )}

        {sessionNotice && (
          <Alert
            id="sc-session_notice_alert"
            severity="error"
            action={
              showRefreshButton ? (
                <Button
                  id="sc-session_refresh_button"
                  color="inherit"
                  size="small"
                  disabled={isRefreshing}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              ) : null
            }
          >
            {sessionNoticeText}
          </Alert>
        )}

        {canRenderClockUi && !isClockConnectErrorBlocking && (
          <>
            <ButtonPad />
            <Footer
              onMissPunchClick={
                !showMissingPunchAlert ? handleMissPunchClick : handleAlertClose
              }
            />
          </>
        )}
      </StyledCard>
      <PoweredByFooter whiteLabelState={whiteLabelState} />
    </GradientContainer>
  );
}
